// src/utils/refreshToken.js

import { gql } from "@apollo/client";
import { authStore } from "./tokenStore";

let refreshPromise = null;

const REFRESH_MUTATION = gql`
  mutation RefreshToken($refreshToken: String!) {
    refreshJwtAuthToken(input: { jwtRefreshToken: $refreshToken }) {
      authToken
    }
  }
`;

// Query to fetch new expiration using user database ID
const GET_USER_EXPIRATION_QUERY = gql`
  query GetUserExpiration($id: ID!) {
    user(id: $id, idType: DATABASE_ID) {
      jwtAuthExpiration
    }
  }
`;

const hasJwtErrorText = (text = "") => {
  const m = String(text).toLowerCase();
  return (
    m.includes("expired token") ||
    m.includes("invalid-secret-key") ||
    m.includes("invalid token") ||
    m.includes("jwt") ||
    m.includes("signature")
  );
};

/**
 * performRefresh(client)
 * - client: ApolloClient instance (must be the same client as your app)
 *
 * This function:
 * - Runs the refresh mutation (skipRetryLink)
 * - If authToken returned: saves token immediately
 * - Then queries the user's jwtAuthExpiration with network-only (skipRetryLink)
 * - Saves new expiration and returns the new token
 */
export const performRefresh = async (client) => {
  const auth = authStore.load();
  if (!auth?.refreshToken) {
    if (process.env.NODE_ENV !== "production") {
      console.log("[performRefresh] ❌ No refresh token stored");
    }
    return null;
  }

  // Avoid parallel refreshes
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    if (process.env.NODE_ENV !== "production") {
      console.log("[performRefresh] 🔄 Starting refresh flow...");
    }

    try {
      // 1) Execute refresh mutation (skip retry link to avoid recursion)
      const { data: refreshData, errors: refreshErrors } = await client.mutate({
        mutation: REFRESH_MUTATION,
        variables: { refreshToken: auth.refreshToken },
        fetchPolicy: "no-cache",
        context: { skipRetryLink: true },
      });

      if (process.env.NODE_ENV !== "production") {
        console.log("[performRefresh] refresh mutation result:", {
          refreshData,
          refreshErrors,
        });
      }

      // If GraphQL-level errors exist, check if they're JWT-related and fail
      if (refreshErrors?.length) {
        const txt = JSON.stringify(refreshErrors).toLowerCase();
        if (process.env.NODE_ENV !== "production") {
          console.log(
            "[performRefresh] refresh mutation errors:",
            refreshErrors
          );
        }
        if (hasJwtErrorText(txt)) {
          throw new Error(
            "Fatal refresh error (refresh mutation returned JWT error)"
          );
        }
      }

      const newAuthToken = refreshData?.refreshJwtAuthToken?.authToken;
      if (!newAuthToken) {
        if (process.env.NODE_ENV !== "production") {
          console.log(
            "[performRefresh] ❗ No authToken returned from refresh mutation"
          );
        }
        throw new Error("Invalid refresh payload (no authToken)");
      }

      // Immediately save the new token (so subsequent requests use it)
      const updatedAuth = {
        ...auth,
        authToken: newAuthToken,
      };
      authStore.save(updatedAuth);
      if (process.env.NODE_ENV !== "production") {
        console.log("[performRefresh] ✅ Saved new authToken");
      }

      // 2) Now fetch new jwtAuthExpiration using the user.databaseId (network-only)
      const userId = auth?.user?.databaseId;
      if (!userId) {
        if (process.env.NODE_ENV !== "production") {
          console.log(
            "[performRefresh] ⚠️ No user.databaseId available; skipping expiration fetch"
          );
        }
        return newAuthToken;
      }

      const { data: expirationData, errors: expErrors } = await client.query({
        query: GET_USER_EXPIRATION_QUERY,
        variables: { id: userId },
        fetchPolicy: "network-only",
        context: { skipRetryLink: true },
      });

      if (process.env.NODE_ENV !== "production") {
        console.log("[performRefresh] expiration query result:", {
          expirationData,
          expErrors,
        });
      }

      if (expErrors?.length) {
        const txt = JSON.stringify(expErrors).toLowerCase();
        if (process.env.NODE_ENV !== "production") {
          console.log("[performRefresh] expiration query errors:", expErrors);
        }
        if (hasJwtErrorText(txt)) {
          throw new Error(
            "Fatal refresh error (expiration query returned JWT error)"
          );
        }
      }

      const newJwtAuthExpiration = expirationData?.user?.jwtAuthExpiration;
      if (newJwtAuthExpiration) {
        const finalAuth = {
          ...updatedAuth,
          jwtAuthExpiration: newJwtAuthExpiration,
        };
        authStore.save(finalAuth);
        if (process.env.NODE_ENV !== "production") {
          console.log(
            "[performRefresh] ✅ Saved new jwtAuthExpiration:",
            newJwtAuthExpiration
          );
        }
      } else {
        if (process.env.NODE_ENV !== "production") {
          console.log("[performRefresh] ⚠️ expiration not found in response");
        }
      }

      return newAuthToken;
    } catch (err) {
      // Any error in refresh flow results in clearing auth (forced logout)
      if (process.env.NODE_ENV !== "production") {
        console.error(
          "[performRefresh] 🔴 Refresh failed, clearing auth:",
          err
        );
      }
      try {
        authStore.clear();
      } catch (e) {
        // ignore
      }
      if (typeof window !== "undefined") {
        try {
          window.localStorage.removeItem("auth");
        } catch (e) {
          // ignore
        }
      }
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};
